import React, { useState, useEffect, useCallback } from 'react';
import { Button, Card, Tag, Empty, Spin, Typography, Space, Divider, Grid } from 'antd';
import {
  GoogleOutlined, LogoutOutlined, ReloadOutlined,
  ShopOutlined, EnvironmentOutlined, ToolOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const API_BASE = process.env.REACT_APP_API_BASE || '';

const BRAND = 'var(--brand-primary, #222957)';
const BORDER = '#E5E7F5';
const HEADING = '#222A5A';

const getToken = () => {
  try { return localStorage.getItem('customer_token'); } catch { return null; }
};

// 付款狀態 → 標籤
const paymentTag = (status) => {
  if (status === 'paid') return <Tag color="green">已付款</Tag>;
  if (status === 'cod') return <Tag color="blue">貨到付款</Tag>;
  if (status === 'failed') return <Tag color="red">付款失敗</Tag>;
  if (status === 'pending') return <Tag color="orange">未付款</Tag>;
  return <Tag>未付款</Tag>;
};

// 訂單處理狀態
const orderStatusLabel = (status) => {
  if (status === 'processing') return '處理中';
  if (status === 'completed') return '已完成';
  if (status === 'cancelled') return '已取消';
  return '待處理';
};

// 物流狀態顯示（後端可能回中文字串或代碼；有值就顯示）
const logisticsLabel = (o) => {
  if (o.logistics_status) return o.logistics_status;
  return null;
};

// 訂購內容摘要
const itemSummary = (o) => {
  if (o.selected_bracelet_name) return o.selected_bracelet_name;
  if (Array.isArray(o.selected_crystals) && o.selected_crystals.length > 0) {
    const names = o.selected_crystals.map((c) => c.crystal_name).filter(Boolean);
    if (names.length > 0) return `客製手鏈 · ${names.join('、')}`;
    return '客製手鏈';
  }
  if (Array.isArray(o.selected_colors) && o.selected_colors.length > 0) {
    return `客製手鏈 · ${o.selected_colors.join('、')}`;
  }
  return null;
};

export default function MyOrders() {
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const navigate = useNavigate();

  const [customer, setCustomer] = useState(null);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [orders, setOrders] = useState(null);
  const [loading, setLoading] = useState(false);

  // 從網址參數處理登入導回（帶 ctoken），並取得客人資訊
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const ctoken = q.get('ctoken');
    if (ctoken) {
      try { localStorage.setItem('customer_token', ctoken); } catch {}
      q.delete('ctoken');
      const rest = q.toString();
      window.history.replaceState(null, '', window.location.pathname + (rest ? `?${rest}` : ''));
    }
    const token = ctoken || getToken();
    if (!token) { setCheckingAuth(false); return; }
    fetch(`${API_BASE}/api/auth/google/customer/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : null))
      .then((info) => {
        if (info) setCustomer(info);
        else { try { localStorage.removeItem('customer_token'); } catch {} }
      })
      .catch(() => {})
      .finally(() => setCheckingAuth(false));
  }, []);

  // 載入自己的訂單
  const loadOrders = useCallback(() => {
    const token = getToken();
    if (!token) return;
    setLoading(true);
    fetch(`${API_BASE}/api/bracelet-orders/my`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : { items: [] }))
      .then((d) => setOrders(d.items || []))
      .catch(() => setOrders([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (customer) loadOrders();
  }, [customer, loadOrders]);

  const startCustomerLogin = useCallback(() => {
    window.location.href = `${API_BASE}/api/auth/google/customer/login?next=${encodeURIComponent('/orders')}`;
  }, []);

  const logoutCustomer = useCallback(() => {
    try { localStorage.removeItem('customer_token'); } catch {}
    setCustomer(null);
    setOrders(null);
  }, []);

  // 未付款訂單：重新導向綠界付款頁（沿用客製表單的付款流程）
  const repay = useCallback((order) => {
    window.location.href = `${API_BASE}/api/payments/ecpay/checkout/${order.id}?ctoken=${encodeURIComponent(localStorage.getItem('customer_token') || '')}`;
  }, []);

  const containerStyle = {
    maxWidth: isMobile ? '100%' : 960,
    margin: '0 auto',
    padding: isMobile ? '16px' : '24px',
    position: 'relative',
    zIndex: 1,
  };

  const rowStyle = { display: 'flex', justifyContent: 'space-between', gap: 8, marginTop: 6 };

  // 尚在驗證登入
  if (checkingAuth) {
    return (
      <div style={{ ...containerStyle, textAlign: 'center', paddingTop: 80 }}>
        <Spin size="large" />
      </div>
    );
  }

  // 未登入：顯示登入提示
  if (!customer) {
    return (
      <div style={containerStyle}>
        <div style={{ textAlign: 'center', marginBottom: 24, paddingTop: 24 }}>
          <Title level={3} style={{ color: '#222A5A', fontWeight: 800 }}>我的訂單</Title>
          <Text type="secondary">用 Google 登入後，即可查看綁定在您信箱下的訂單與狀態</Text>
        </div>
        <div style={{ textAlign: 'center' }}>
          <Button
            type="primary" size="large" icon={<GoogleOutlined />}
            onClick={startCustomerLogin}
            style={{ height: 48, fontSize: 16 }}
          >
            登入查看我的訂單
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <style>{`
        .order-card { transition: transform .18s ease, box-shadow .18s ease; }
        @media (hover: hover) { .order-card:hover { transform: translateY(-4px); box-shadow: 0 10px 24px rgba(34,41,87,0.16); } }
      `}</style>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 12, marginBottom: 8 }}>
        <Title level={3} style={{ margin: 0, color: BRAND, fontWeight: 800, letterSpacing: 1 }}>我的訂單</Title>
        <Space>
          <Button size="small" icon={<ReloadOutlined />} onClick={loadOrders} loading={loading}>重新整理</Button>
        </Space>
      </div>
      <Divider style={{ margin: '12px 0' }} />

      {loading && orders === null ? (
        <div style={{ textAlign: 'center', padding: '48px 0' }}>
          <Spin size="large" />
        </div>
      ) : !(orders && orders.length > 0) ? (
        <Empty
          description="目前沒有訂單"
          style={{ padding: '48px 0' }}
        >
          <Button type="primary" href="/custom">開始設計我的手鏈</Button>
        </Empty>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, minmax(0, 1fr))',
            gap: 16,
            alignItems: 'start',
          }}
        >
        {orders.map((o) => {
          const unpaid = o.payment_status !== 'paid' && o.payment_status !== 'cod';
          const canRepay = unpaid && o.payment_method === 'ecpay';
          const summary = itemSummary(o);
          const logistics = logisticsLabel(o);
          return (
            <Card
              key={o.id}
              className="order-card"
              onClick={() => navigate(`/orders/${o.order_code}`)}
              style={{ borderRadius: 16, borderColor: BORDER, cursor: 'pointer' }}
              bodyStyle={{ padding: 16 }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                <Space wrap>
                  <Tag color="geekblue" style={{ fontSize: 13, fontWeight: 600 }}>{o.order_code}</Tag>
                  <Tag>{orderStatusLabel(o.status)}</Tag>
                </Space>
                {paymentTag(o.payment_status)}
              </div>

              {summary && (
                <div style={{ marginTop: 10 }}>
                  <Text strong style={{ color: HEADING }}>{summary}</Text>
                </div>
              )}

              <div style={rowStyle}>
                <Text type="secondary">成立時間</Text>
                <Text>{dayjs(o.created_at).format('YYYY-MM-DD HH:mm')}</Text>
              </div>

              {o.amount != null && (
                <div style={rowStyle}>
                  <Text type="secondary">金額</Text>
                  <Text strong style={{ color: 'var(--brand-primary)' }}>NT$ {o.amount}</Text>
                </div>
              )}

              {(o.cvs_store_name || o.logistics_type) && (
                <div style={rowStyle}>
                  <Text type="secondary"><ShopOutlined /> 取貨門市</Text>
                  <Text style={{ textAlign: 'right' }}>{o.cvs_store_name || '超商取貨'}</Text>
                </div>
              )}

              {logistics && (
                <div style={rowStyle}>
                  <Text type="secondary"><EnvironmentOutlined /> 物流狀態</Text>
                  <Text style={{ textAlign: 'right' }}>{logistics}</Text>
                </div>
              )}

              {o.logistics_no && (
                <div style={rowStyle}>
                  <Text type="secondary">物流單號</Text>
                  <Text copyable style={{ textAlign: 'right' }}>{o.logistics_no}</Text>
                </div>
              )}

              {canRepay && (
                <Button
                  type="primary" block style={{ marginTop: 14, height: 44, borderRadius: 10 }}
                  onClick={(e) => { e.stopPropagation(); repay(o); }}
                >
                  繼續付款{o.amount != null ? ` NT$ ${o.amount}` : ''}
                </Button>
              )}

              {o.status === 'completed' ? (
                <Button
                  block icon={<ToolOutlined />}
                  style={{ marginTop: 10, height: 40, borderRadius: 10 }}
                  onClick={(e) => { e.stopPropagation(); navigate(`/repair?order_code=${encodeURIComponent(o.order_code)}`); }}
                >
                  申請售後維修
                </Button>
              ) : (
                <Button
                  block disabled icon={<ToolOutlined />}
                  style={{ marginTop: 10, height: 40, borderRadius: 10 }}
                >
                  訂單完成後可申請售後
                </Button>
              )}
            </Card>
          );
        })}
        </div>
      )}
    </div>
  );
}
